import React from 'react';
import { Sparkles, Plus, ArrowLeft, ChevronRight } from 'lucide-react';
import { cn } from '../../lib/utils';

/**
 * Start-picker primitives — THE shared visual language for every per-type
 * start experience (flowchart, org chart, genogram two-step, future
 * network/...). Extracted from FlowchartStartPicker so the pickers stay
 * uniform by construction instead of by copy-paste.
 *
 * House tokens only: coral accents, navy text, ink neutrals. Full-canvas
 * overlay (replaces the canvas like the genogram views), never a modal.
 */

interface DataEvent {
  click: string;
  params?: Record<string, unknown>;
}

const dataEventAttrs = (ev?: DataEvent) =>
  ev
    ? {
        'data-event-click': ev.click,
        ...(ev.params ? { 'data-event-params': JSON.stringify(ev.params) } : {}),
      }
    : {};

export const StartPickerShell: React.FC<{
  /** Header glyph inside the coral badge. */
  icon: React.ReactNode;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  testId: string;
  /** Rendered above the header — e.g. a StartPickerBackLink. */
  topSlot?: React.ReactNode;
  /** Footer note under the content. */
  footer?: React.ReactNode;
  /** Width of the centered column; default matches the flowchart picker. */
  maxWidthClass?: string;
  /** Tightened padding when embedded in a canvas dialog. */
  compact?: boolean;
  children: React.ReactNode;
}> = ({ icon, title, subtitle, testId, topSlot, footer, maxWidthClass = 'max-w-2xl', compact = false, children }) => (
  <div
    className={cn(
      'flex min-h-full items-center justify-center bg-gradient-to-br from-[color:var(--color-ink-50)] to-white',
      compact ? 'p-4' : 'p-6',
    )}
    data-testid={testId}
  >
    <div className={cn('w-full', maxWidthClass)}>
      {topSlot}
      <div className="mb-5 text-center">
        {/* [&_svg]:!size-5 — passed-in glyphs may carry their own size
            classes (e.g. the genogram use-case icons' size-6 lg:size-12);
            the badge normalizes them so every picker header matches. */}
        <div className="mb-2 inline-flex size-10 items-center justify-center rounded-[12px] bg-[color:var(--color-coral-100)] text-[color:var(--color-coral-600)] [&_svg]:!size-5">
          {icon}
        </div>
        <h2 className="text-[20px] font-bold text-[color:var(--color-navy-900)]">{title}</h2>
        {subtitle && (
          <p className="mt-1 text-[13px] text-[color:var(--color-ink-500)]">{subtitle}</p>
        )}
      </div>
      {children}
      {footer && (
        <div className="mt-5 text-center text-[12px] text-[color:var(--color-ink-500)]">{footer}</div>
      )}
    </div>
  </div>
);

export const StartPickerBackLink: React.FC<{
  label: React.ReactNode;
  onClick: () => void;
  testId?: string;
}> = ({ label, onClick, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className="mb-4 flex items-center gap-1.5 text-[12px] font-medium text-[color:var(--color-ink-500)] transition-colors hover:text-[color:var(--color-coral-600)]"
  >
    <ArrowLeft className="size-3.5" />
    {label}
  </button>
);

/** The featured AI row — "Generate from text" / "Describe it". */
export const StartPickerGenerateButton: React.FC<{
  title: React.ReactNode;
  description: React.ReactNode;
  onClick: () => void;
  testId?: string;
  dataEvent?: DataEvent;
}> = ({ title, description, onClick, testId, dataEvent }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    {...dataEventAttrs(dataEvent)}
    className={cn(
      'mb-4 flex w-full items-center gap-3 rounded-[12px] border border-[color:var(--color-coral-200)] bg-[color:var(--color-coral-50)] px-4 py-3 text-left transition-all',
      'hover:border-[color:var(--color-coral-300)] hover:shadow-[0_2px_10px_rgba(244,96,76,0.12)]',
    )}
  >
    <span className="flex size-9 shrink-0 items-center justify-center rounded-[10px] bg-[color:var(--color-coral-500)] text-white">
      <Sparkles className="size-5" />
    </span>
    <span className="min-w-0 flex-1">
      <span className="flex items-center gap-2">
        <span className="text-[14px] font-semibold text-[color:var(--color-navy-900)]">{title}</span>
        <span className="rounded-full bg-[color:var(--color-coral-100)] px-1.5 py-px text-[9px] font-bold uppercase tracking-wide text-[color:var(--color-coral-700)]">
          AI
        </span>
      </span>
      <span className="block text-[12px] text-[color:var(--color-ink-500)]">{description}</span>
    </span>
    <ChevronRight className="size-5 shrink-0 text-[color:var(--color-coral-500)]" />
  </button>
);

/** A starter card — icon (or richer preview node), name, description,
 *  optional footer (e.g. audience chips). */
export const StartPickerCard: React.FC<{
  /** Glyph or preview rendered in the navy icon well. */
  icon: React.ReactNode;
  name: React.ReactNode;
  description: React.ReactNode;
  /** Optional bottom slot — chips, meta. */
  footer?: React.ReactNode;
  onClick: () => void;
  testId?: string;
  dataEvent?: DataEvent;
}> = ({ icon, name, description, footer, onClick, testId, dataEvent }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    {...dataEventAttrs(dataEvent)}
    className={cn(
      'flex flex-col items-start gap-2 rounded-[12px] border border-[color:var(--color-ink-200)] bg-white p-3 text-left transition-all',
      'hover:-translate-y-0.5 hover:border-[color:var(--color-coral-300)] hover:shadow-[0_4px_12px_rgba(13,42,77,0.08)]',
    )}
  >
    <span className="flex h-10 w-full items-center justify-start">
      {/* [&_svg]:!size-6 — normalize glyphs that carry their own size classes. */}
      <span className="flex size-10 items-center justify-center rounded-[10px] bg-[color:var(--color-navy-100)] text-[color:var(--color-navy-700)] [&_svg]:!size-6">
        {icon}
      </span>
    </span>
    <span className="text-[13px] font-semibold text-[color:var(--color-navy-900)]">{name}</span>
    <span className="text-[11px] leading-[1.35] text-[color:var(--color-ink-500)]">{description}</span>
    {footer && <span className="mt-auto flex w-full flex-wrap gap-1 pt-1">{footer}</span>}
  </button>
);

/** Small neutral chip for StartPickerCard footers (audiences, tags). */
export const StartPickerChip: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="rounded-full bg-[color:var(--color-ink-100)] px-2 py-px text-[10px] font-medium text-[color:var(--color-ink-600)]">
    {children}
  </span>
);

/** The dashed "Blank" card. */
export const StartPickerBlankCard: React.FC<{
  name: React.ReactNode;
  description: React.ReactNode;
  onClick: () => void;
  testId?: string;
  dataEvent?: DataEvent;
}> = ({ name, description, onClick, testId, dataEvent }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    {...dataEventAttrs(dataEvent)}
    className={cn(
      'flex flex-col items-start gap-2 rounded-[12px] border border-dashed border-[color:var(--color-ink-300)] bg-white p-3 text-left transition-all',
      'hover:-translate-y-0.5 hover:border-[color:var(--color-coral-300)] hover:shadow-[0_4px_12px_rgba(13,42,77,0.08)]',
    )}
  >
    <span className="flex size-10 items-center justify-center rounded-[10px] bg-[color:var(--color-ink-100)] text-[color:var(--color-ink-500)]">
      <Plus className="size-6" />
    </span>
    <span className="text-[13px] font-semibold text-[color:var(--color-navy-900)]">{name}</span>
    <span className="text-[11px] leading-[1.35] text-[color:var(--color-ink-500)]">{description}</span>
  </button>
);
